// Click-a-header-to-sort, shared by every master-data table on References and
// on Rules (owner, 2026-09-01: "make the column header clickable, so clicking
// it sorts the table alphabetically by that value").
//
// The state and the comparison live here; the header cell that drives them is
// `components/SortableHeaderCell.tsx`. They are split because the two screens
// render their tables differently — References goes through `MasterTable`,
// Rules hand-rolls each `<Table>` — and one sorting behaviour across both is
// the whole point.
//
// THE SORT IS CLIENT-SIDE AND REORDERS WHAT IS ON SCREEN. On Vendors that
// means the current page of the current search, not the whole 6,210-row
// master; the server decides which rows arrive, this decides their order.
import { useCallback, useMemo, useState } from 'react'

type SortDirection = 'asc' | 'desc'

/** Which column is sorted, and which way. `null` is the unsorted state a table
    opens in — the order the manager loaded, which is meaningful on its own. */
type TableSortState<K extends string> = { key: K; direction: SortDirection } | null

/** Alphabetical the way a person means it: case- and accent-insensitive, and
    digit runs compared as numbers so `62-90` sorts before `62-100`. */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

export function compareText(a: string, b: string): number {
  return collator.compare(a, b)
}

interface TableSort<K extends string> {
  state: TableSortState<K>
  /** Header click: the same column flips direction, a new one starts ascending. */
  toggle: (key: K) => void
  /** What `TableHeaderCell` wants for its arrow — `undefined` on every other column. */
  directionFor: (key: K) => 'ascending' | 'descending' | undefined
  /** Put the sort back to a given state — the screen's own default, or `null`
      for the order the rows arrived in. Assignments calls it when the tab
      changes: its five screens have different columns, and a sort carried
      across is not the default the new screen means to open on. */
  reset: (to: TableSortState<K>) => void
}

export function useTableSort<K extends string>(initial: TableSortState<K> = null): TableSort<K> {
  const [state, setState] = useState<TableSortState<K>>(initial)
  // A pure updater, deliberately: React re-runs updaters in StrictMode, so the
  // direction flip has to be part of the same state value rather than a second
  // setter called from inside this one.
  const toggle = useCallback((key: K) => {
    setState((current) =>
      current && current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    )
  }, [])
  const directionFor = useCallback(
    (key: K): 'ascending' | 'descending' | undefined => {
      if (state?.key !== key) return undefined
      return state.direction === 'asc' ? 'ascending' : 'descending'
    },
    [state],
  )
  const reset = useCallback((to: TableSortState<K>) => setState(to), [])
  return useMemo(
    () => ({ state, toggle, directionFor, reset }),
    [state, toggle, directionFor, reset],
  )
}

/** Orders `rows` by the sorted column's own reader. A column with no reader in
    `keys` is not sortable and leaves the list untouched. */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  state: TableSortState<K>,
  keys: Partial<Record<K, (row: T) => string>>,
): readonly T[] {
  if (!state) return rows
  const read = keys[state.key]
  if (!read) return rows
  const factor = state.direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => factor * compareText(read(a), read(b)))
}
